import * as Sentry from '@sentry/react';
import { User, House } from '@/client';

/**
 * Sets the current user context for all subsequent Sentry reports.
 * Should be called after successful login and cleared on logout.
 */
export const setSentryUser = (user: User | null) => {
	if (user) {
		Sentry.setUser({
			id: user.id.toString(),
			email: user.email,
		});
	} else {
		Sentry.setUser(null);
	}
};

/**
 * Sets the current house context for all subsequent Sentry reports.
 * Should be called when the selected house changes.
 */
export const setSentryHouseContext = (house: House | null) => {
	if (house) {
		Sentry.setContext('house', {
			id: house.id,
			name: house.name || 'Unnamed Property',
			address: house.address,
		});
	} else {
		Sentry.setContext('house', null);
	}
};

/**
 * Reports an error to Sentry with optional house and additional context.
 * Use this for controlled error reporting with full context.
 */
export const reportError = (
	error: Error | string,
	options?: {
		level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
		tags?: Record<string, string>;
		extra?: Record<string, unknown>;
		fingerprint?: string[];
	},
) => {
	const errorToReport = typeof error === 'string' ? new Error(error) : error;

	Sentry.captureException(errorToReport, {
		level: options?.level || 'error',
		tags: options?.tags,
		extra: options?.extra,
		fingerprint: options?.fingerprint,
	});
};

/**
 * Reports a message to Sentry (non-error event).
 * Useful for tracking important application events.
 */
export const reportMessage = (
	message: string,
	options?: {
		level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
		tags?: Record<string, string>;
		extra?: Record<string, unknown>;
	},
) => {
	Sentry.captureMessage(message, {
		level: options?.level || 'info',
		tags: options?.tags,
		extra: options?.extra,
	});
};

/**
 * Adds a breadcrumb to the current Sentry scope.
 * Useful for tracking the sequence of events leading to an error.
 */
export const addBreadcrumb = (message: string, data?: Record<string, unknown>, category?: string) => {
	Sentry.addBreadcrumb({
		message,
		data,
		category: category || 'app',
		level: 'info',
	});
};
